import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../lib/api';
import { buildConsentPayload } from '../lib/consentPayload';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      // Fetch user info (token will be added automatically by interceptor)
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  // Синхронизация с axios interceptor: при 401 он чистит localStorage — обновляем React state,
  // иначе isAuthenticated остаётся true без токена в LS → «Not authenticated» на /cart.
  useEffect(() => {
    const syncFromStorage = () => {
      const t = localStorage.getItem('token');
      setToken(t || null);
      if (!t) {
        setUser(null);
      }
    };
    window.addEventListener('auth-tokens-cleared', syncFromStorage);
    return () => window.removeEventListener('auth-tokens-cleared', syncFromStorage);
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user info:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        const headers = error.config?.headers || {};
        const authHeader =
          headers.Authorization ?? headers.authorization ?? '';
        const reqTok =
          typeof authHeader === 'string' && authHeader.startsWith('Bearer ')
            ? authHeader.slice(7).trim()
            : '';
        const lsTok = localStorage.getItem('token');
        // Не сбрасываем новый токен из-за ответа на запрос со старым JWT или без заголовка (гонка)
        if (reqTok && lsTok && reqTok !== lsTok) {
          setLoading(false);
          return;
        }
        if (!reqTok && lsTok) {
          setLoading(false);
          return;
        }
        localStorage.removeItem('token');
        setToken(null);
      }
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { access_token } = response.data;
      
      if (!access_token) {
        throw new Error('Токен не получен от сервера');
      }
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      // Fetch user info (token will be added automatically by interceptor)
      try {
        await fetchUserInfo();
      } catch (fetchError) {
        console.error('Error fetching user info after login:', fetchError);
        // Не прерываем процесс входа, если получение информации о пользователе не удалось
        // Токен уже сохранен, пользователь может продолжить работу
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Ошибка при входе';
      
      if (error.response) {
        // Сервер вернул ошибку
        errorMessage = error.response.data?.detail || error.response.data?.message || `Ошибка ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        // Запрос был сделан, но ответ не получен
        errorMessage = 'Не удалось подключиться к серверу. Проверьте подключение к интернету.';
      } else {
        // Что-то пошло не так при настройке запроса
        errorMessage = error.message || 'Ошибка при входе';
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const register = async (email, password, consent = false, marketingConsent = false) => {
    try {
      const response = await apiClient.post('/auth/register', {
        email,
        password,
        ...buildConsentPayload(consent, marketingConsent),
      });
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      // Fetch user info (token will be added automatically by interceptor)
      await fetchUserInfo();
      
      return { success: true };
    } catch (error) {
      console.error('Register error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Ошибка при регистрации'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
